import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import {
  ABSpinner,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import axios from 'axios'
import API from '../../utils/API'
import ExperienceDetails from './ExperienceDetails'
import HeaderImage from './HeaderImage'
import ExperienceDates from './ExperienceDates'
import QuestAdditionalDetails from './QuestAdditionalDetails'
import QuestInvestment from './QuestInvestment'
import QuestPartners from './QuestPartners'
import QuestInclusions from './QuestInclusions'
import QuestTravelChecklist from './QuestTravelChecklist'
import QuestEnrollmentProcess from './QuestEnrollmentProcess'
import QuestMainVideo from './QuestMainVideo'
import QuestTestimonials from './QuestTestimonials'
import QuestJourney from './QuestJourney'
import QuestFaculty from './QuestFaculty'
import QuestFAQs from './QuestFAQs'
import QuestSliderImages from './QuestSliderImages'
import CustomiseOnboarding from './CustomiseOnboarding'
import QuestWelcomeEmail from './QuestWelcomeEmail'
import ExpressInterestForm from './ExpressInterestForm'
import RsvpInfoForm from './RsvpInfoForm'
import QuestOrientationCall from './QuestOrientationCall'
import CopyQuestModal from './CopyQuestModal'
import ExperienceCopyModal from './ExperienceCopyModal'
import QuestsItinerary from './QuestsItinerary'
import QuestsLearning from './QuestsLearning'
import QuestsEnrollmentTypeform from './QuestEnrollmentTypeform'
import QuestGuidePairing from './QuestGuidePairing'
import ExperienceBuildRail from './ExperienceBuildRail'
import ExperienceAccordionSection from './ExperienceAccordionSection'
import ExperienceWorkspace from './ExperienceWorkspace'
import ExperienceButton from './ExperienceButton'
import {
  buildExperiencePath,
  getBuildItemByKey,
  getBuildStage,
  getNextBuildItem,
} from './experienceNavigation'
import { getBuildStageMeta, getBuildItemMeta } from './buildCompleteness'
import { isWelcomeEmailComplete } from './enrollmentValidation'
import { UTCformatDateInPlan } from '../../utils/date'
import { flushActiveBuildForm, useSetupNameMissing } from './questAutosave'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

const sectionComponents = [
  ExperienceDetails,
  HeaderImage,
  QuestSliderImages,
  ExperienceDates,
  QuestInvestment,
  ExpressInterestForm,
  RsvpInfoForm,
  QuestAdditionalDetails,
  QuestPartners,
  QuestInclusions,
  CustomiseOnboarding,
  QuestWelcomeEmail,
  QuestMainVideo,
  QuestTestimonials,
  QuestJourney,
  QuestFaculty,
  QuestFAQs,
  QuestTravelChecklist,
  QuestOrientationCall,
  QuestEnrollmentProcess,
]

const countEmptyItineraryDays = (days) =>
  Array.isArray(days)
    ? days.filter(
        (d) =>
          !(
            Array.isArray(d?.itineraries) &&
            d.itineraries.some(
              (e) =>
                (typeof e?.itinerary === 'string' && e.itinerary.trim()) ||
                (typeof e?.title === 'string' && e.title.trim()) ||
                e?.fromTime,
            )
          ),
      ).length
    : 0

const STAGE_SUMMARIES = {
  setup: 'Set up',
  sell: 'Content',
  enrol: 'Enrollment',
  travel: 'Participant prep',
}

const ExperienceBuild = ({
  questDetail,
  setQuestDetail,
  isLoading,
  activeItemKey,
  onSelectItem,
  openStageKey,
  onStageToggle,
  onSaveStateChange,
  onClientReadinessChange,
  isSaving = false,
  isNewlyCopied = false,
}) => {
  const Error = useErrorService()
  const { questId } = useParams()
  const history = useHistory()
  const location = useLocation()
  const Notification = useNotificationService()
  const [isCopying, setIsCopying] = useState(false)
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [showCopyPicker, setShowCopyPicker] = useState(false)
  const [selectedQuestForCopy, setSelectedQuestForCopy] = useState(null)
  const [expandedKeys, setExpandedKeys] = useState({})
  const [isGoingNext, setIsGoingNext] = useState(false)
  const skipNextScrollRef = useRef(false)
  const railLoadedForRef = useRef(null)
  // After stage/page load we only open the first accordion; ignore the
  // coinciding activeItem effect so deep links (e.g. content-video) stay closed.
  const skipAccordionExpandForStageRef = useRef(null)
  const signupRef = useRef(null)
  const welcomeEmailRef = useRef(null)
  const enrolFormRef = useRef(null)
  const itineraryRef = useRef(null)
  const checklistRef = useRef(null)
  const [isSendingEmailTest, setIsSendingEmailTest] = useState(false)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
  const [railExtras, setRailExtras] = useState({})
  // Forms / itinerary / email load after questDetail — don't score rail issues
  // until every rail-extra API has settled (avoids "2 issues" → "1 issue" flash).
  const [railExtrasReady, setRailExtrasReady] = useState(!questId)
  const openCopyFlowRequested = !!(
    location &&
    location.state &&
    location.state.openCopyFlow
  )
  // Only the "Copy from another experience" create path keeps the Build copy
  // tool. Scratch starts blank — no global copy control that implies copy-anywhere.
  const [showCopyTool, setShowCopyTool] = useState(openCopyFlowRequested)

  const activeItem = getBuildItemByKey(activeItemKey || 'setup-name')
  // URL/active item is source of truth. Prefer it over openStageKey so a
  // deep link (Overview → Fix) is not briefly treated as the previous stage
  // (which used to history.replace back to Experience Details).
  const activeStageKey = activeItem.stageKey || openStageKey || 'setup'
  const stage = getBuildStage(activeStageKey)
  const nextItem = getNextBuildItem(activeItem.key)
  const setupNameMissing = useSetupNameMissing()
  const isNextBlocked = activeItem.key === 'setup-name' && setupNameMissing
  const isAccordion = stage.layout === 'accordion'

  const stageGroups = useMemo(() => {
    return (
      stage.groups || [
        {
          key: 'default',
          kicker: stage.title || stage.label,
          description: stage.description,
          itemKeys: stage.items.map((i) => i.key),
        },
      ]
    )
  }, [stage])

  useEffect(() => {
    if (activeItem.stageKey && activeItem.stageKey !== openStageKey) {
      onStageToggle(activeItem.stageKey, { forceOpen: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem.key])

  useEffect(() => {
    if (
      activeItem.key !== 'prep-schedule' &&
      activeItem.key !== 'prep-checklist'
    ) {
      setIsDownloadingPdf(false)
    }
  }, [activeItem.key])

  // Forms / invite / email / learning live outside questDetail — hydrate rail notes.
  useEffect(() => {
    if (!questId || !questDetail?._id) {
      setRailExtras({})
      setRailExtrasReady(true)
      return undefined
    }

    let cancelled = false
    // Only flag "not ready" for the first load of an experience; refetching
    // after each save stays silent so the rail/pills don't flash "Checking…".
    if (railLoadedForRef.current !== questDetail._id) {
      setRailExtrasReady(false)
    }

    const loadRailExtras = async () => {
      const extras = {}

      try {
        const [
          formRes,
          inquiryRes,
          emailRes,
          journeyRes,
          resourcesRes,
          itineraryRes,
        ] = await Promise.all([
          API.ClientAPI.quests.getCustomForm(questId).catch(() => null),
          API.ClientAPI.quests
            .getCustomForm(questId, 'questInquiry')
            .catch(() => null),
          API.AdminAPI.quests.getQuestWelcomeEmail(questId).catch(() => null),
          API.AdminAPI.quests.getQuestJourney({ questId }).catch(() => null),
          API.AdminAPI.quests.getQuestResources(questId).catch(() => null),
          API.AdminAPI.quests.getItinerary(questId).catch(() => null),
        ])

        const formData = formRes?.data
        if (formData) {
          const questions = (formData.questions || []).filter(
            (q) => !q.isDeleted,
          )
          extras.customFormId = formData._id || formData.id || null
          extras.enrollmentQuestions = questions
          extras.enrollmentQuestionCount = questions.length
        }

        const inquiryData = inquiryRes?.data
        if (inquiryData && inquiryData.type === 'questInquiry') {
          const questions = (inquiryData.questions || []).filter(
            (q) => !q.isDeleted,
          )
          extras.expressInterestForm = { questions }
        }

        const emailTemplate = emailRes?.data?.emailTemplate || ''
        if (isWelcomeEmailComplete(emailTemplate)) {
          extras.welcomeEmailHtml = emailTemplate
          extras.emailTemplate = emailTemplate
        } else {
          extras.welcomeEmailHtml = ''
          extras.emailTemplate = ''
        }

        const journeyData = journeyRes?.data
        if (journeyData && typeof journeyData === 'object') {
          const audios = Array.isArray(journeyData.audios)
            ? journeyData.audios
            : []
          extras.audios = audios
          extras.journeyAudios = audios
        }

        const resourcesData = resourcesRes?.data
        if (resourcesData && typeof resourcesData === 'object') {
          extras.videos = Array.isArray(resourcesData.videos)
            ? resourcesData.videos
            : []
          extras.blogs = Array.isArray(resourcesData.blogs)
            ? resourcesData.blogs
            : []
        }

        const itineraryData = itineraryRes?.data
        if (itineraryData && typeof itineraryData === 'object') {
          const itineraryDays = Array.isArray(itineraryData.days)
            ? itineraryData.days
            : []
          extras.itinerary = itineraryDays
          extras.itineraryDays = itineraryDays
        }

        const railNetworkId =
          typeof questDetail.networkId === 'object'
            ? questDetail.networkId?._id || questDetail.networkId?.id
            : questDetail.networkId
        if (railNetworkId) {
          try {
            const { data } =
              await API.AdminAPI.entityService.getEntityInviteLinkInfo(
                'network',
                railNetworkId,
              )
            if (data) {
              extras.welcomeLetter = data.welcomeLetter || ''
              extras.signupHeader = data.header || ''
              extras.signupBody = data.body || ''
              extras.signupTitle = data.title || ''
              extras.signupPageContent = data.body || ''
            }
          } catch (e) {
            // Invite details are optional for rail scoring.
          }
        }
      } finally {
        if (!cancelled) {
          railLoadedForRef.current = questDetail._id
          // Merge so stats a child page already reported aren't wiped.
          setRailExtras((prev) => ({ ...prev, ...extras }))
          setRailExtrasReady(true)
        }
      }
    }

    loadRailExtras()
    return () => {
      cancelled = true
    }
  }, [questId, questDetail?._id, questDetail?.networkId])

  const completenessSource = useMemo(
    () => ({
      ...(questDetail || {}),
      ...railExtras,
      // Prefer questDetail stepConfiguration; extras should not wipe it.
      stepConfiguration:
        questDetail?.stepConfiguration || railExtras.stepConfiguration,
    }),
    [questDetail, railExtras],
  )

  useEffect(() => {
    if (!openCopyFlowRequested) {
      return undefined
    }
    setShowCopyTool(true)
    setShowCopyPicker(true)
    history.replace({
      pathname: location.pathname,
      state: { ...(location.state || {}), openCopyFlow: false },
    })
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCopyFlowRequested])

  // Accordion: on stage entry, expand the deep-linked / selected item when it
  // belongs to this stage. Only fall back to the first card (and sync the URL)
  // when the active rail key is outside the stage.
  useEffect(() => {
    if (!isAccordion) {
      return
    }
    const firstKey =
      (stageGroups[0] &&
        stageGroups[0].itemKeys &&
        stageGroups[0].itemKeys[0]) ||
      (stage.items && stage.items[0] && stage.items[0].key)
    const stageItemKeys = [
      ...(stage.items || []).map((item) => item.key),
      ...(stage.optionalItems || []).map((item) => item.key),
    ]
    const activeInStage = stageItemKeys.includes(activeItem.key)

    if (activeInStage) {
      setExpandedKeys({ [activeItem.key]: true })
      // Allow the scroll/expand effect to focus the deep-linked section.
      skipAccordionExpandForStageRef.current = null
      return
    }

    setExpandedKeys(firstKey ? { [firstKey]: true } : {})
    skipAccordionExpandForStageRef.current = activeStageKey
    if (firstKey && activeItem.key !== firstKey) {
      if (questId) {
        history.replace(buildExperiencePath(questId, 'build', firstKey))
      } else {
        onSelectItem(firstKey)
      }
    }
    // Only reset when the stage changes — not on every rail click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStageKey, isAccordion])

  // Accordion: expand the selected rail item. Soft-scroll when coming from
  // the rail (not accordion header clicks — those stay in place).
  useEffect(() => {
    if (!isAccordion) {
      return
    }
    // Page load / stage switch: keep only the first accordion open.
    if (skipAccordionExpandForStageRef.current === activeStageKey) {
      skipAccordionExpandForStageRef.current = null
      return
    }
    setExpandedKeys((prev) => ({
      ...prev,
      [activeItem.key]: true,
    }))
    if (skipNextScrollRef.current) {
      skipNextScrollRef.current = false
      return
    }
    const t = setTimeout(() => {
      const el = document.getElementById(activeItem.key)
      if (!el) {
        return
      }
      const scroller = el.closest('.experience-editor-main')
      if (scroller) {
        const elRect = el.getBoundingClientRect()
        const scrollerRect = scroller.getBoundingClientRect()
        const inView =
          elRect.top >= scrollerRect.top + 8 &&
          elRect.bottom <= scrollerRect.bottom - 8
        if (!inView) {
          const top = elRect.top - scrollerRect.top + scroller.scrollTop - 12
          scroller.scrollTo({ top, behavior: 'smooth' })
        }
        return
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 40)
    return () => clearTimeout(t)
  }, [activeItem.key, isAccordion])

  const handleQuestSelection = (selectedId, selectedQuest) => {
    if (!selectedId) return
    setSelectedQuestForCopy({
      value: selectedId,
      label: selectedQuest?.name || selectedQuest?.title || '',
      extra: selectedQuest,
    })
    setShowCopyPicker(false)
    setShowCopyModal(true)
  }

  const handleCopyPickerSelect = (option) => {
    if (!option?.value) return
    handleQuestSelection(option.value, option.extra || option)
  }

  const handleConfirmCopy = async (
    selectedId,
    selectedQuest,
    questName,
    formValues,
  ) => {
    if (!selectedId || !questName) return
    try {
      setIsCopying(true)
      const body = {
        existingQuestId: selectedId,
        questName: questName.trim(),
        ...(formValues?.questStartDate && {
          questStartDate: UTCformatDateInPlan(formValues.questStartDate),
        }),
        ...(formValues?.questEndDate && {
          questEndDate: UTCformatDateInPlan(formValues.questEndDate),
        }),
        itinerary: formValues.itinerary || [],
      }
      const { data } = await API.AdminAPI.quests.copyQuest(body)
      if (data && data._id) {
        setShowCopyModal(false)
        setSelectedQuestForCopy(null)
        history.replace({
          pathname: `/admin/experiences/${data._id}/overview`,
          state: {
            prefill: data,
            isNewlyCopied: true,
            copiedFromTitle:
              selectedQuest?.name ||
              selectedQuest?.title ||
              selectedQuest?.label ||
              '',
          },
        })
        try {
          sessionStorage.setItem(
            `experience-copied-${data._id}`,
            JSON.stringify({
              copiedFromTitle:
                selectedQuest?.name ||
                selectedQuest?.title ||
                selectedQuest?.label ||
                '',
            }),
          )
        } catch (storageError) {
          // ignore storage failures
        }
        Notification.showNotification(
          'Experience copied successfully!',
          'success',
        )
      }
    } catch (e) {
      Error.showError(e)
    } finally {
      setIsCopying(false)
    }
  }

  const handleCloseModal = () => {
    if (!isCopying) {
      setShowCopyModal(false)
      setSelectedQuestForCopy(null)
    }
  }

  const addUpdateQuest = async (values, options) => {
    try {
      if (typeof onSaveStateChange === 'function') {
        onSaveStateChange('saving')
      }
      const { data } = await API.AdminAPI.quests.updateQuests(values)
      setQuestDetail(data)
      if (!questId) {
        // Keep the section the user is on (first save can come from any step).
        history.replace(
          location.pathname.includes('/create-new')
            ? location.pathname.replace('/create-new', `/${data._id}`)
            : `/admin/experiences/${data._id}/build/setup-name`,
        )
      }
      // Header already shows "Saving…" / "All changes saved" — skip toast.
      // const silent =
      //   options && typeof options === 'object' ? !!options.silent : false
      // if (!silent) {
      //   Notification.showNotification('Experience has been updated!', 'success')
      // }
      if (typeof onSaveStateChange === 'function') {
        onSaveStateChange('saved')
      }
      return Promise.resolve(data)
    } catch (e) {
      Error.showError(e)
      if (typeof onSaveStateChange === 'function') {
        onSaveStateChange('error')
      }
      return Promise.reject(e)
    }
  }

  const uploadImage = async (imageFile, key = null) => {
    try {
      const contentType = imageFile.name
        .substr(imageFile.name.lastIndexOf('.') + 1)
        .toUpperCase()

      if (!questId) {
        Notification.showNotification('Title is Required', 'danger')
        return
      }

      const { data } = await API.AdminAPI.quests.getQuestImage(
        questId,
        contentType,
      )
      if (data) {
        await axios.put(data.url.url, imageFile, {
          headers: {
            'Content-Type': data.url.contentType,
          },
        })
        await addUpdateQuest({ [key]: data.fileName, id: questId })
        return data.fileName
      }
    } catch (e) {
      Error.showError(e)
      throw e
    }
  }

  const stageMeta = useMemo(
    () =>
      getBuildStageMeta(completenessSource, {
        isNewQuest: !questId || !questDetail?._id,
        isNewlyCopied,
        railExtrasReady,
      }),
    [
      completenessSource,
      questId,
      questDetail?._id,
      isNewlyCopied,
      railExtrasReady,
    ],
  )

  // Keep header Enrollment / Departure pills in sync with the Build rail.
  const lastReadinessRef = useRef(null)
  useEffect(() => {
    if (typeof onClientReadinessChange !== 'function') return
    if (!railExtrasReady) return
    const enrol = stageMeta?.enrol
    const travel = stageMeta?.travel
    const sell = stageMeta?.sell
    const next = {
      prosperity: sell
        ? {
            ok: !!sell.done,
            issues: sell.issueCount || 0,
          }
        : null,
      enrollment: enrol
        ? {
            ok: !!enrol.done,
            issues: enrol.issueCount || 0,
          }
        : null,
      prep: travel
        ? {
            ok: !!travel.done,
            issues: travel.issueCount || 0,
            itemIssues: travel.itemIssues || {},
            emptyItineraryDays: countEmptyItineraryDays(
              railExtras.itineraryDays,
            ),
            unpairedCount: Math.max(
              0,
              (railExtras.guidePairingStats?.total || 0) -
                (railExtras.guidePairingStats?.pairedCount || 0),
            ),
          }
        : null,
    }
    // Only notify the parent when the readiness actually changed; a fresh
    // object each run re-rendered the parent and looped this effect.
    const serialized = JSON.stringify(next)
    if (lastReadinessRef.current === serialized) return
    lastReadinessRef.current = serialized
    onClientReadinessChange(next)
  }, [stageMeta, railExtrasReady, railExtras, onClientReadinessChange])

  const isNewQuest = !questDetail || !questDetail._id

  const renderWorkspaceActions = () => {
    switch (activeItem.key) {
      case 'enrol-signup':
        return (
          <ExperienceButton
            variant='outline'
            onClick={() => {
              const openPreview =
                signupRef.current && signupRef.current.openPreview
              if (typeof openPreview === 'function') {
                openPreview()
              }
            }}>
            Preview
          </ExperienceButton>
        )
      case 'enrol-email':
        return (
          <ExperienceButton
            variant='primary'
            size='sm'
            disabled={isSendingEmailTest}
            onClick={async () => {
              const sendTest =
                welcomeEmailRef.current && welcomeEmailRef.current.sendTest
              if (typeof sendTest !== 'function' || isSendingEmailTest) {
                return
              }
              setIsSendingEmailTest(true)
              try {
                await sendTest()
              } finally {
                setIsSendingEmailTest(false)
              }
            }}>
            {isSendingEmailTest ? 'Sending…' : 'Send test to myself'}
          </ExperienceButton>
        )
      case 'enrol-form':
        return (
          <>
            {(railExtras.enrollmentQuestionCount || 0) === 0 ? (
              <ExperienceButton
                variant='outline'
                size='sm'
                onClick={() => {
                  const openCopyFrom =
                    enrolFormRef.current && enrolFormRef.current.openCopyFrom
                  if (typeof openCopyFrom === 'function') {
                    openCopyFrom()
                  }
                }}>
                Copy from
              </ExperienceButton>
            ) : null}
            <ExperienceButton
              variant='outline'
              size='sm'
              onClick={() => {
                const openPreview =
                  enrolFormRef.current && enrolFormRef.current.openPreview
                if (typeof openPreview === 'function') {
                  openPreview()
                }
              }}>
              Preview
            </ExperienceButton>
          </>
        )
      case 'prep-schedule':
        return (
          <>
            <ExperienceButton
              variant='outline'
              disabled={isDownloadingPdf}
              onClick={() => itineraryRef.current?.previewPdf?.()}>
              Preview PDF
            </ExperienceButton>
            <ExperienceButton
              variant='outline'
              disabled={isDownloadingPdf}
              onClick={() => itineraryRef.current?.downloadPdf?.()}>
              {isDownloadingPdf ? 'Downloading...' : 'Download PDF'}
            </ExperienceButton>
            <ExperienceButton
              variant='outline'
              disabled={isDownloadingPdf}
              onClick={() => itineraryRef.current?.openSendNotification?.()}
              title='Sends a mobile ping to every participant that the itinerary has changed and they can view the latest. It does not say what changed.'>
              Send Notification
            </ExperienceButton>
          </>
        )
      case 'prep-checklist':
        return (
          <>
            <ExperienceButton
              variant='outline'
              disabled={isDownloadingPdf}
              onClick={() => checklistRef.current?.previewPdf?.()}>
              Preview PDF
            </ExperienceButton>
            <ExperienceButton
              variant='outline'
              disabled={isDownloadingPdf}
              onClick={() => checklistRef.current?.downloadPdf?.()}>
              {isDownloadingPdf ? 'Downloading...' : 'Download PDF'}
            </ExperienceButton>
            <ExperienceButton
              variant='primary'
              disabled={isDownloadingPdf}
              onClick={() => checklistRef.current?.addSection?.()}>
              Add section
            </ExperienceButton>
          </>
        )
      default:
        return null
    }
  }

  const renderSectionContent = (item) => {
    if (item.type === 'page') {
      if (item.page === 'enrollment-typeform') {
        return (
          <QuestsEnrollmentTypeform
            ref={enrolFormRef}
            questDetail={questDetail}
            onQuestionsChange={(questions) => {
              const list = Array.isArray(questions) ? questions : []
              setRailExtras((prev) => {
                if (
                  prev.enrollmentQuestions === list &&
                  prev.enrollmentQuestionCount === list.length
                ) {
                  return prev
                }
                return {
                  ...prev,
                  enrollmentQuestions: list,
                  enrollmentQuestionCount: list.length,
                  customFormId:
                    prev.customFormId || (list.length > 0 ? true : null),
                }
              })
            }}
          />
        )
      }
      if (item.page === 'itinerary') {
        return (
          <QuestsItinerary
            ref={itineraryRef}
            questTitle={questDetail?.title}
            onSaveStateChange={onSaveStateChange}
            onPdfDownloadStateChange={setIsDownloadingPdf}
            onCompletenessChange={(patch) => {
              if (!patch || typeof patch !== 'object') return
              setRailExtras((prev) => ({ ...prev, ...patch }))
            }}
          />
        )
      }
      if (item.page === 'learning') {
        return (
          <QuestsLearning
            questName={questDetail?.name}
            onSaveStateChange={onSaveStateChange}
            onCompletenessChange={(patch) => {
              if (!patch || typeof patch !== 'object') return
              setRailExtras((prev) => ({ ...prev, ...patch }))
            }}
          />
        )
      }
      if (item.page === 'guide-pairing') {
        return (
          <QuestGuidePairing
            questDetail={questDetail}
            enrollmentQuestions={railExtras.enrollmentQuestions}
            onCompletenessChange={(patch) => {
              if (!patch || typeof patch !== 'object') return
              setRailExtras((prev) => ({ ...prev, ...patch }))
            }}
          />
        )
      }
      return null
    }
    const Section = sectionComponents[item.sectionIndex]
    if (!Section) {
      return null
    }
    const sectionRef =
      item.key === 'enrol-signup'
        ? signupRef
        : item.key === 'enrol-email'
        ? welcomeEmailRef
        : item.key === 'prep-checklist'
        ? checklistRef
        : null

    return React.createElement(Section, {
      ref: sectionRef,
      questDetail,
      addUpdateQuest,
      uploadImage,
      isSaving,
      onSaveStateChange,
      onPdfDownloadStateChange:
        item.key === 'prep-checklist' ? setIsDownloadingPdf : undefined,
      type: 'network',
      networkId:
        typeof questDetail?.networkId === 'object'
          ? questDetail.networkId?._id || questDetail.networkId?.id
          : questDetail?.networkId,
      onCompletenessChange: (patch) => {
        if (!patch || typeof patch !== 'object') return
        setRailExtras((prev) => ({ ...prev, ...patch }))
      },
    })
  }

  const toggleAccordion = (key) => {
    setExpandedKeys((prev) => {
      const nextOpen = !prev[key]
      if (nextOpen) {
        // Accordion header click — update rail selection but do not jump scroll
        skipNextScrollRef.current = true
        onSelectItem(key)
      }
      return { ...prev, [key]: nextOpen }
    })
  }

  const itemsByKey = useMemo(() => {
    const map = {}
    ;[...stage.items, ...(stage.optionalItems || [])].forEach((item) => {
      map[item.key] = item
    })
    return map
  }, [stage])

  const renderAccordionPanel = () => {
    return (
      <>
        <div className='experience-ptitle'>
          <h2>{stage.title || stage.label}</h2>
        </div>
        {stage.description ? (
          <p className='experience-psub'>{stage.description}</p>
        ) : null}
        {stageGroups.map((group) => (
          <div key={group.key} className='experience-build-group'>
            <div className='experience-build-kicker'>{group.kicker}</div>
            {group.description ? <p>{group.description}</p> : null}
            {group.itemKeys.map((key) => {
              const item = itemsByKey[key]
              if (!item) {
                return null
              }
              const expanded = expandedKeys[item.key] === true
              return (
                <ExperienceAccordionSection
                  key={item.key}
                  id={item.key}
                  title={item.label}
                  where={item.where}
                  meta={getBuildItemMeta(item.key, completenessSource)}
                  expanded={expanded}
                  onToggle={() => toggleAccordion(item.key)}>
                  {renderSectionContent(item)}
                </ExperienceAccordionSection>
              )
            })}
          </div>
        ))}
      </>
    )
  }

  const renderWorkspacePanel = () => (
    <ExperienceWorkspace
      kicker={STAGE_SUMMARIES[activeItem.stageKey] || 'Build'}
      title={activeItem.label}
      where={activeItem.where}
      meta={getBuildItemMeta(activeItem.key, completenessSource)}
      description={activeItem.description}
      actions={renderWorkspaceActions()}>
      {renderSectionContent(activeItem)}
    </ExperienceWorkspace>
  )

  // Wait until quest detail (incl. networkId) is ready — otherwise Signup
  // mounts with an empty quest and shows a false "no network" error.
  if (questId && (isLoading || !questDetail?._id)) {
    return <ABSpinner />
  }

  return (
    <>
      <Helmet>
        <title>{`Experience · Build | Abroad`}</title>
      </Helmet>
      <div className='experience-build-layout'>
        <div className='experience-cols'>
          <ExperienceBuildRail
            activeStageKey={activeStageKey}
            activeItemKey={activeItem.key}
            disabled={isSaving}
            onStageToggle={(key) => {
              if (isSaving) return
              onStageToggle(key)
              const first = getBuildStage(key).items[0]
              if (first) {
                onSelectItem(first.key)
              }
            }}
            onSelectItem={(key) => {
              if (isSaving) return
              onSelectItem(key)
            }}
            stageMeta={stageMeta}
          />
          <div className='experience-editor-pane'>
            <div className='experience-editor-main'>
              {isNewQuest && showCopyTool ? (
                <div className='experience-tools mb-4'>
                  <button
                    type='button'
                    className='btn-outline-experience'
                    disabled={isCopying || isSaving}
                    onClick={() => setShowCopyPicker(true)}>
                    Copy from another experience ▾
                  </button>
                </div>
              ) : null}
              {isAccordion ? renderAccordionPanel() : renderWorkspacePanel()}
            </div>
          </div>
        </div>
        <div className='experience-contbar'>
          <div className='experience-prog'>
            <span>
              {STAGE_SUMMARIES[activeItem.stageKey] || 'Build'} · changes save
              automatically
            </span>
          </div>
          {nextItem ? (
            <OverlayTrigger
              placement='top'
              show={isNextBlocked ? undefined : false}
              overlay={
                <Tooltip id='next-blocked-tip'>
                  Enter an internal name to continue
                </Tooltip>
              }>
              <span
                className='d-inline-block'
                style={{ cursor: isNextBlocked ? 'not-allowed' : 'pointer' }}>
                <button
                  type='button'
                  className='btn-navy'
                  disabled={isGoingNext || isCopying || isNextBlocked}
                  style={isNextBlocked ? { pointerEvents: 'none' } : undefined}
                  onMouseDown={(e) => {
                    // Keep focus until onClick so blur→isSaving can't disable
                    // this button before the click fires.
                    e.preventDefault()
                  }}
                  onClick={async () => {
                    if (!nextItem || isGoingNext || isCopying) return
                    setIsGoingNext(true)
                    try {
                      const isValid = await flushActiveBuildForm()
                      if (isValid === false) return
                      onSelectItem(nextItem.key)
                    } finally {
                      setIsGoingNext(false)
                    }
                  }}>
                  {isGoingNext
                    ? 'Saving…'
                    : `Next: ${nextItem.stageLabel}: ${nextItem.label} →`}
                </button>
              </span>
            </OverlayTrigger>
          ) : (
            <button
              type='button'
              className='btn-navy'
              disabled={isGoingNext || isCopying}
              onMouseDown={(e) => e.preventDefault()}
              onClick={async () => {
                if (isGoingNext || isCopying) return
                setIsGoingNext(true)
                try {
                  const isValid = await flushActiveBuildForm()
                  if (isValid === false) return
                  history.push(buildExperiencePath(questId, 'manage'))
                } finally {
                  setIsGoingNext(false)
                }
              }}>
              {isGoingNext ? 'Saving…' : 'Build complete'}
            </button>
          )}
        </div>
      </div>
      <ExperienceCopyModal
        show={showCopyPicker}
        onHide={() => {
          if (!isCopying) setShowCopyPicker(false)
        }}
        onSelect={handleCopyPickerSelect}
        title='Copy from another experience'
        lead='The new draft receives content, enrollment and participant prep. Participants, payments and inquiries are not copied.'
        isLoadingSelection={isCopying}
      />
      <CopyQuestModal
        show={showCopyModal}
        onHide={handleCloseModal}
        selectedQuest={selectedQuestForCopy}
        onConfirmCopy={handleConfirmCopy}
        isLoading={isCopying}
      />
    </>
  )
}

export default ExperienceBuild
